import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, CardContent, Chip, Grid, Stack, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { API_BASE_URL, getAuthHeaders, getStoredUser } from '../auth';
import { ErrorBanner, LoadingPane, PageHeader } from '../components/page-elements';

const getOrderStatusChipProps = (status) => {
  switch (status) {
    case 'Draft':
      return { color: 'warning', variant: 'filled' };
    case 'Submitted':
      return { color: 'info', variant: 'filled' };
    case 'Shipped':
    case 'Completed':
      return { color: 'success', variant: 'filled' };
    case 'Cancelled':
      return { color: 'error', variant: 'outlined' };
    default:
      return { color: 'default', variant: 'filled' };
  }
};

const renderRefName = (value, fallback = 'N/A') => {
  if (!value) return fallback;
  if (typeof value === 'string') return value;
  return (
    value.fullName ||
    value.customerName ||
    value.receiverName ||
    value.projectName ||
    value.shipperName ||
    value.materialName ||
    fallback
  );
};

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return 'N/A';
  return dt.toLocaleString();
};

const renderPreferredRailcars = (order) => {
  if (order?.inventorySource === 'ground') {
    const primaryLot = order?.groundInventoryLot?._id || order?.groundInventoryLot || 'Ground Lot';
    const secondaryLot = order?.secondaryGroundInventoryLot?._id || order?.secondaryGroundInventoryLot || 'Ground Lot';
    if (!order?.splitLoad) return primaryLot;
    return `${primaryLot} + ${secondaryLot}`;
  }

  const primary = order?.railcarID || 'Determined by Site';
  const secondary = order?.secondaryRailcarID || 'Determined by Site';
  if (!order?.splitLoad) return primary;
  return `${primary} + ${secondary}`;
};

const DetailRow = ({ label, value }) => (
  <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ py: 0.4 }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" fontWeight={600} textAlign="right">{value || 'N/A'}</Typography>
  </Stack>
);

const OrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useMemo(() => getStoredUser(), []);
  const isCustomer = user?.role === 'customer';

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) return;

    setLoading(true);
    setError('');
    fetch(`${API_BASE_URL}/api/orders/${id}`, {
      headers: getAuthHeaders(),
    })
      .then((response) => {
        if (!response.ok) throw new Error('Failed to load order details');
        return response.json();
      })
      .then((data) => setOrder(data))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <LoadingPane label="Loading order details..." />;

  if (!order) {
    return (
      <Box p={2}>
        <ErrorBanner message={error || 'Order not found'} />
        <Button variant="outlined" onClick={() => navigate('/orders')}>Back to Orders</Button>
      </Box>
    );
  }

  return (
    <Box p={2}>
      <PageHeader
        title={`Order ${order.orderNumber || ''}`.trim()}
        subtitle="Order details and scheduling information."
        actions={[
          <Button key="back" variant="outlined" onClick={() => navigate('/orders')}>
            Back to Orders
          </Button>,
          isCustomer && (order.orderStatus === 'Draft' || order.orderStatus === 'Submitted') ? (
            <Button key="edit" variant="contained" onClick={() => navigate(`/orders/create?orderId=${order._id}`)}>
              {order.orderStatus === 'Submitted' ? 'Edit Submitted' : 'Edit Draft'}
            </Button>
          ) : null,
        ].filter(Boolean)}
      />
      <ErrorBanner message={error} />

      <Grid container spacing={1.5}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.8 }}>Core Details</Typography>
              <DetailRow label="Order Number" value={order.orderNumber} />
              <DetailRow label="Customer" value={renderRefName(order.customerName)} />
              <DetailRow label="Status" value={<Chip size="small" label={order.orderStatus || 'Draft'} {...getOrderStatusChipProps(order.orderStatus || 'Draft')} />} />
              <DetailRow label="Order Date" value={formatDateTime(order.orderDate)} />
              <DetailRow label="Created By" value={renderRefName(order.createdBy, 'N/A')} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.8 }}>Routing</Typography>
              <DetailRow label="Receiver" value={renderRefName(order.receiverName)} />
              <DetailRow label="Location" value={renderRefName(order.projectName)} />
              <DetailRow label="Shipper" value={renderRefName(order.shipperName)} />
              <DetailRow label="Material" value={renderRefName(order.materialName)} />
              <DetailRow label="Preferred Railcar(s)" value={renderPreferredRailcars(order)} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.8 }}>Schedule & Notes</Typography>
              <Grid container spacing={1}>
                <Grid item xs={12} md={4}>
                  <DetailRow label="Pick Up Date" value={formatDateTime(order.pickUpDate)} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DetailRow label="Delivery Date" value={formatDateTime(order.deliveryDate)} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DetailRow label="Access Code" value={order.accessCode || 'N/A'} />
                </Grid>
              </Grid>
              <Box mt={1}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.3 }}>Notes</Typography>
                <Typography variant="body2">{order.notes || 'No notes provided.'}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OrderDetails;
